import type { FlexProps, MotionPropsFor } from '@hover/blueprint';
import {
  Body,
  Flex,
  Heading,
  Link,
  motion,
  transition,
  useTheme,
} from '@hover/blueprint';
import { iconShop } from '@hover/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { imageTracking } from 'src/modules/homeownerApp/components/Customize/tracking';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import type { AiGeneratedImageFragment } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useTimeout,
  useTranslate,
} from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { isDevelopment } from 'src/shared/utils/environment';

import { GOOGLE_LENS_URL, HOVER_URL } from '../../constants';

const GOT_IT_KEY = 'canShopGotIt';

interface LensButtonProps extends MotionPropsFor<FlexProps> {
  image: AiGeneratedImageFragment;
}

const MotionFlex = motion(Flex);

const EXPANDED_WIDTH = '224px';
const EXPANDED_HEIGHT = '192px';

const COLLAPSE_DELAY = 750;

export const LensButton = ({ image, ...props }: LensButtonProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isHoveringTimeout, clearIsHoveringTimeout] = useTimeout();

  const [gotIt, setGotIt] = useState(false);

  const { trackingData } = useHomeownerAppContext();
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { projectId } = useParams<{ projectId: string }>();

  const imageUrl = isDevelopment()
    ? `${HOVER_URL}${image?.generatedImagePath}`
    : image?.generatedImagePath;
  const lensUrl = `${GOOGLE_LENS_URL}${imageUrl}`;

  const trackingProperties = useMemo(
    () => ({
      ...trackingData,
      ...imageTracking(image),
      partner_id: partnerId,
      primary_cta: false,
      project_id: Number(projectId),
      destination_url: lensUrl,
      link_text: 'shopping-basket-icon',
      track_id: 'shop-image-link',
    }),
    [image, lensUrl, partnerId, projectId, trackingData],
  );

  const impressionProperties = useMemo(
    () => ({
      ...trackingProperties,
      button_text: 'shopping-basket-icon',
      track_id: 'shop-image-button',
    }),
    [trackingProperties],
  );

  const handleGotItClick = () => {
    setGotIt(true);
    localStorage.setItem(GOT_IT_KEY, 'true');
  };

  useEffect(() => {
    try {
      setGotIt(localStorage.getItem(GOT_IT_KEY) === 'true');
    } catch (e) {
      localStorage.removeItem(GOT_IT_KEY);
    }
  }, []);

  const {
    motion: { framer },
  } = useTheme();

  const isExpanded = isHovering && !gotIt;

  return (
    <MotionFlex
      _hover={{ background: 'darkAlpha.400' }}
      alignItems="end"
      animate={{
        height: isExpanded ? EXPANDED_HEIGHT : 'auto',
        width: isExpanded ? EXPANDED_WIDTH : 'auto',
      }}
      backdropFilter="blur(50px)"
      background="darkAlpha.300"
      borderRadius="350"
      boxSizing="border-box"
      initial={false}
      justifyContent="end"
      onMouseEnter={() => {
        clearIsHoveringTimeout();
        setIsHovering(true);
      }}
      onMouseLeave={() =>
        isHoveringTimeout(() => setIsHovering(false), COLLAPSE_DELAY)
      }
      overflow="hidden"
      position="relative"
      sx={transition.easing250('background')}
      transition={{ ...framer.firm, damping: 20 }}
      {...props}
    >
      <MotionFlex
        alignItems="start"
        animate={isExpanded ? 'visible' : 'hidden'}
        boxSizing="border-box"
        direction="column"
        flex={1}
        height={EXPANDED_HEIGHT}
        left={0}
        position="absolute"
        px="500"
        py="600"
        top={0}
        variants={{
          visible: { opacity: 1, transition: { delay: 0.1, duration: 0.3 } },
          hidden: { opacity: 0, transition: { duration: 0.1 } },
        }}
        width={EXPANDED_WIDTH}
      >
        <Heading color="inherit" size="xs">
          {t('fullscreenImage.shopDesignIdeas')}
        </Heading>
        <Body color="inherit" m={0} size="small">
          {t('fullscreenImage.seeSomethingYouLike')}
        </Body>
        <Link
          _hover={{ opacity: 0.7 }}
          as="button"
          color="inherit"
          cursor="pointer"
          display="inline"
          mt="1em"
          onClick={handleGotItClick}
          p={0}
          size="small"
          textDecoration="underline"
          {...transition.easing250('opacity')}
        >
          {t('fullscreenImage.gotIt')}
        </Link>
      </MotionFlex>
      <Button
        _hover={{ svg: gotIt ? {} : { opacity: 0.7 } }}
        as="a"
        background="none !important"
        border="none !important"
        borderRadius="inherit"
        data-group
        href={lensUrl}
        icon={iconShop}
        includeTrackingImpression
        label={t('fullscreenImage.shopDesignIdeas')}
        m={0}
        onClick={e => e.stopPropagation()}
        optionalImpressionProperties={impressionProperties}
        optionalTrackingProperties={trackingProperties}
        sx={{ svg: transition.easing250('opacity') }}
        target="_blank"
      />
    </MotionFlex>
  );
};
